import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import RightTimeApi from "@/API";
import Request from "../elements/Request";
import ProctorProvidedFieldsBar from "@/elements/ProctorProvidedFieldsBar";
import { hasRequiredJobField, getSitesForRequestContext } from "@/utils/formSelectionOptions";
import { shouldShowRequestPage, canAutoAdvanceTakerRequestPage } from "@/utils/requestPageHelpers";
import { renderFormattedContent } from "@/utils/renderFormattedContent";
import TestSections from "../sections/TestSections";
import RestartPageOverlay from "../sections/RestartPageOverlay";
import { clearSectionDraft } from "@/utils/sectionDraftStorage";
import { collectClientEnvironment } from "@/utils/clientEnvironment";
import { apiMessage } from "@/utils/apiMessage";
import { startTestPath } from "@/utils/startTestPath";
import {
    clearSessionStoragePreservingTakerSession,
    clearTakerSessionActiveOnReload,
    clearTakerTestIdentity,
    getStoredTakerCttId,
    getStoredTakerEntryCode,
    getStoredTakerTestCode,
    hasActiveTakerSession,
    isCttEntryCode,
    isStartTestResumableForCtt,
    isTestEntryCode,
    markTakerSessionActive,
    persistTakerTestIdentity,
    readCttRestartableFromStartTest,
    resolveCttEntryResumeAction,
    resolveNonRestartableRevisitAction,
    shouldClearStoredTakerDataForTestCode,
    shouldClearTakerTestIdentity,
    stripStartTestResumeHints,
} from "@/utils/takerTestStorage";
import {
    buildRestartedRealtimePostData,
    resolveResumeSection,
    sectionResumeFor,
    shouldRecordSilentRestart,
    suppressRestartForActiveSession,
} from "@/utils/restartRealtime";
import { toast } from "react-toastify";

export { startTestPath } from "@/utils/startTestPath";

export default function Start() {
    const { code: routeCode } = useParams(); // /Start/:code
    const [searchParams] = useSearchParams(); // /Start?code=...
    const initialCode = routeCode || searchParams.get("code") || "";
    const startOver =
        searchParams.get("startOver") === "1" ||
        searchParams.get("startOver")?.toLowerCase() === "true";
    const [code, updateCode] = useState(initialCode);
    const [errorMessages, updateMessages] = useState([]);
    const [showSpinner, updateSpinner] = useState(false);
    const [testData, updateTestData] = useState<any>(null);
    const [requestFieldValues, setRequestFieldValues] = useState<Record<string, unknown> | null>(null);
    const [currentTestType, updateCurrentTestType] = useState<any>(null);
    const [restartContinueBusy, setRestartContinueBusy] = useState(false);
    const requestAdvanceAttemptedRef = useRef(false);
    // After a fork/mint, we already have CreateAsync payload for the new code — skip the
    // follow-up StartTest GET. Re-fetching a non-restartable forked test (StartTime already set)
    // would fork again and loop, leaving Welcome blank with Next disabled.
    const skipStartTestFetchForCodeRef = useRef<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (skipStartTestFetchForCodeRef.current === code) {
            skipStartTestFetchForCodeRef.current = null;
            requestAdvanceAttemptedRef.current = false;
            void reportClientEnvironmentAsync(code);
            return;
        }
        getTestDataAsync();
        requestAdvanceAttemptedRef.current = false;
    }, [code]);

    useEffect(() => {
        if (!testData) return;
        testData.isDebug = false;
        const firstTestType = Array.isArray(testData.testTypes) ? testData.testTypes[0] : undefined;
        updateCurrentTestType(firstTestType ?? null);

        if (testData.languageId && Array.isArray(testData.languages)) {
            const lang = testData.languages.find((l: any) => l.id === testData.languageId);
            if (lang)
                testData.lang = lang.abbrev;
        }
        if (checkForMessages(testData).length == 0) {
            clearSessionStoragePreservingTakerSession();
            const resolvedCode = String(testData?.testData?.code || testData?.code || code || '').trim();
            const resolvedCttId = Number(testData?.testData?.companyTestTypeId || 0);
            const entryCodeForPersist = isCttEntryCode(code)
                ? code
                : isCttEntryCode(getStoredTakerEntryCode())
                    ? undefined
                    : code;

            // Fork / CTT mint: API returned a different Test code than the URL — switch identity and navigate.
            // Keep testData from this response (already the forked CreateAsync payload). Do not
            // re-GET StartTest for the new code — that re-forks when Restartable is false.
            if (resolvedCode && isTestEntryCode(resolvedCode) && resolvedCode !== code) {
                const preservedEntry = isCttEntryCode(code)
                    ? code
                    : (isCttEntryCode(getStoredTakerEntryCode()) ? getStoredTakerEntryCode() : undefined);
                localStorage.clear();
                persistTakerTestIdentity(
                    resolvedCode,
                    resolvedCttId > 0 ? resolvedCttId : null,
                    preservedEntry
                );
                markTakerSessionActive();
                skipStartTestFetchForCodeRef.current = resolvedCode;
                updateCode(resolvedCode);
                navigate(`/Start/${resolvedCode}`, { replace: true });
                return;
            }

            if (resolvedCode) {
                persistTakerTestIdentity(
                    resolvedCode,
                    resolvedCttId > 0 ? resolvedCttId : null,
                    entryCodeForPersist
                );
            }
        } else {
            const storedCode = getStoredTakerTestCode();
            const storedCttId = getStoredTakerCttId();
            if (shouldClearTakerTestIdentity(testData, storedCode, storedCttId)) {
                clearTakerTestIdentity();
            }
        }
    }, [testData]);

    useEffect(() => {
        if (!currentTestType || !testData) return;
        if (testData.restartPage) return;
        if (!Array.isArray(currentTestType.sections)) return;

        let sortedSections = [...currentTestType.sections].sort((a, b) => a.order - b.order);
        let lastCompletedSectionId = testData.lastSectionIdCompleted;
        if (lastCompletedSectionId) {
            let index = sortedSections.findIndex(s => s.id === lastCompletedSectionId);
            testData.currentSection = sortedSections[index + 1] || null;
        }
        else
            testData.currentSection = sortedSections[0];

    }, [currentTestType, testData?.restartPage]);

    useEffect(() => {
        if (!testData || requestAdvanceAttemptedRef.current) return;
        if (shouldShowRequestPage(testData)) return;
        if (!testData.requiresTesteeData) return;
        if (!canAutoAdvanceTakerRequestPage(testData)) return;
        if ((testData.testData?.stateId ?? 0) >= 200) return;

        requestAdvanceAttemptedRef.current = true;
        void advancePastEmptyRequestPageAsync();
    }, [testData]);

    async function advancePastEmptyRequestPageAsync() {
        const testId = Number(testData?.testData?.id || 0);
        const cttId = Number(testData?.testData?.companyTestTypeId || testData?.testTypes?.[0]?.companyTestType?.id || 0);
        if (!testId || !cttId) return;

        const activeTestType = testData?.testTypes?.find(
            (tt) => Number(tt?.companyTestType?.id) === cttId
        ) ?? testData?.testTypes?.[0];

        const filterOpts = {
            companyTestTypeId: cttId,
            lockedJobId: testData?.testData?.jobId ?? null,
            lockedSiteId: testData?.testData?.siteId ?? null,
        };
        const unionSites = getSitesForRequestContext({
            siteFieldProctorProvided: false,
            testType: activeTestType,
            userRoleSites: testData?.userAccessibleSites,
            eligibleSitesFromServer: testData?.eligibleSitesForTakerSiteSelect,
            companySiteRoleJobs: testData?.companySiteRoleJobs,
            filterOptions: filterOpts,
            requireSitesWithJobs: hasRequiredJobField(testData?.fields, { takerOnly: true }),
            companyJobs: testData?.jobs,
        });

        const payload: Record<string, unknown> = {
            id: testId,
            testId,
            companyTestTypeId: cttId,
            companyId: testData?.companyId,
            code: testData?.testData?.code,
            languageId: testData?.testData?.languageId,
        };
        if (testData?.testData?.siteId) {
            payload.siteId = testData.testData.siteId;
        } else if (unionSites.length === 1) {
            payload.siteId = unionSites[0].siteId;
        }
        if (testData?.testData?.jobId) {
            payload.jobId = testData.testData.jobId;
        }

        updateSpinner(true);
        try {
            const resp = await RightTimeApi.sendAsync("/Test", payload);
            if (resp?.ok && resp.jsonData) {
                updateTestData(resp.jsonData);
            }
        } finally {
            updateSpinner(false);
        }
    }

    async function getTestDataAsync() {
        clearTakerSessionActiveOnReload();

        if (startOver && isCttEntryCode(code)) {
            clearTakerTestIdentity();
        } else if (isCttEntryCode(code)) {
            const storedCode = getStoredTakerTestCode();
            const storedCttId = getStoredTakerCttId();
            const storedCttCode = getStoredTakerEntryCode();
            if (storedCode && storedCttId > 0) {
                // Match stored CTT entry code from localStorage — do not call anonymous CompanyTestTypeGet.
                // Restartable comes from the StartTest probe payload when codes match.
                const matchAction = resolveCttEntryResumeAction({
                    enteredCode: code,
                    storedTestCode: storedCode,
                    storedCttId,
                    storedCttCode,
                    cttRestartable: true,
                });
                if (matchAction === "clear-mismatch") {
                    clearTakerTestIdentity();
                } else if (matchAction === "attempt-resume" || matchAction === "start-fresh") {
                    const probe = await RightTimeApi.getAsync(startTestPath(storedCode, storedCode));
                    const cttRestartable = probe?.ok
                        ? readCttRestartableFromStartTest(probe.jsonData, storedCttId)
                        : false;
                    const action = resolveCttEntryResumeAction({
                        enteredCode: code,
                        storedTestCode: storedCode,
                        storedCttId,
                        storedCttCode,
                        cttRestartable,
                    });
                    if (action === "attempt-resume") {
                        if (probe?.ok && isStartTestResumableForCtt(probe.jsonData, storedCttId)) {
                            updateCode(storedCode);
                            navigate(`/Start/${storedCode}`, { replace: true });
                            return;
                        }
                    } else if (action === "start-fresh") {
                        // Non-restartable CTT: never resume a prior attempt. Start a brand-new test
                        // and clear any leftover browser/session state from the previous test.
                        clearTakerTestIdentity();
                        window.sessionStorage.clear();
                    } else if (action === "clear-mismatch") {
                        clearTakerTestIdentity();
                    }
                }
            }
        } else if (!startOver) {
            const storedEntryCode = getStoredTakerEntryCode();
            const storedTestCode = getStoredTakerTestCode();
            if (shouldClearStoredTakerDataForTestCode(code, storedEntryCode, storedTestCode)) {
                clearTakerTestIdentity();
            }
        }

        updateSpinner(true);
        const storedForStart = getStoredTakerTestCode();
        const resp = await RightTimeApi.getAsync(startTestPath(code, storedForStart));
        updateSpinner(false);
        if (resp && resp.ok && resp.jsonData) {
            const payload = resp.jsonData as Record<string, unknown>;
            const revisitAction = resolveNonRestartableRevisitAction({
                payload,
                requestedCode: code,
                storedEntryCode: getStoredTakerEntryCode(),
                storedTestCode: getStoredTakerTestCode(),
                hasActiveSession: hasActiveTakerSession(),
            });
            if (revisitAction === "redirect-to-ctt-entry") {
                const entryCode = getStoredTakerEntryCode();
                clearTakerTestIdentity();
                window.sessionStorage.clear();
                if (!entryCode) return;
                updateCode(entryCode);
                navigate(`/Start/${entryCode}`, { replace: true });
                return;
            }

            const hasActiveSession = hasActiveTakerSession();
            const testPayload = suppressRestartForActiveSession(
                revisitAction === "suppress-resume"
                    ? stripStartTestResumeHints(payload)
                    : payload,
                hasActiveSession
            );

            updateTestData(testPayload);
            if (shouldRecordSilentRestart(testPayload, hasActiveSession)) {
                const resumeSection = resolveResumeSection(testPayload);
                void RightTimeApi.postAsync("/RealTimeResponse", buildRestartedRealtimePostData(testPayload, resumeSection));
            }
            if (isStartTestInProgressPayload(testPayload)) {
                markTakerSessionActive();
            }
            void reportClientEnvironmentAsync();
        }
    }

    function isStartTestInProgressPayload(payload: Record<string, unknown> | null | undefined): boolean {
        const testData =
            payload?.testData && typeof payload.testData === "object"
                ? (payload.testData as Record<string, unknown>)
                : null;
        const stateId = Number(testData?.stateId || 0);
        return stateId > 0 && stateId < 301;
    }

    async function reportClientEnvironmentAsync(environmentCode: string = code) {
        if (!environmentCode) return;

        try {
            await RightTimeApi.postAsync(`/StartTest/${environmentCode}/Environment`, collectClientEnvironment());
        } catch (error) {
            console.error("Failed to report client environment:", error);
        }
    }

    async function onRestartContinueAsync() {
        const restartPage = testData?.restartPage;
        const sectionId = Number(restartPage?.sectionId || 0);
        const testId = Number(testData?.testData?.id || 0);
        if (!sectionId || !testId) return;

        setRestartContinueBusy(true);
        updateSpinner(true);
        try {
            const resp = await RightTimeApi.postAsync(`/StartTest/${code}/RestartContinue`, { sectionId });
            if (!(resp && resp.ok)) {
                const msg = apiMessage(resp, "Unable to continue the test.");
                toast.error(msg);
                return;
            }

            clearSectionDraft(testId, sectionId);

            const sections = [...(currentTestType?.sections || testData?.testTypes?.[0]?.sections || [])]
                .sort((a, b) => a.order - b.order);
            const targetSection = sections.find((section) => Number(section?.id) === sectionId);
            if (!targetSection) {
                toast.error("Unable to locate the section to continue.");
                return;
            }

            const resumedPage = sectionResumeFor(testData?.sectionResume, sectionId).page;

            const nextSection = {
                ...targetSection,
                page: resumedPage,
            };

            localStorage.setItem('sectionOrder', String(nextSection.order || 0));
            updateTestData({
                ...testData,
                restartPage: null,
                currentSection: nextSection,
            });
        } finally {
            setRestartContinueBusy(false);
            updateSpinner(false);
        }
    }

    function checkForMessages(jsonData) {
        let ret = [];
        if (jsonData) {
            if (jsonData.messages && jsonData.messages.length > 0) {
                // Flatten the array and ensure only strings are added
                ret = ret.concat(jsonData.messages.filter((msg) => typeof msg === "string"));
            }
            const apiText = apiMessage({ jsonData });
            if (apiText) {
                ret.push(apiText);
            }
        }
        if (ret.length > 0) {
            updateMessages((prevMessages) => [...prevMessages, ...ret]);
        }
        return ret;
    }

    function renderActivity() {
        if (testData) {
            const shouldShowRequest = shouldShowRequestPage(testData);
            if (testData.testData?.stateId < 200 && shouldShowRequest) {
                if (
                    hasRequiredJobField(testData.fields, { takerOnly: true }) &&
                    (!Array.isArray(testData.companySiteRoleJobs) || testData.companySiteRoleJobs.length === 0)
                ) {
                    return (
                        <div className="ErrorPage">
                            <p>You do not have permission to take this questionnaire.</p>
                        </div>
                    );
                }
            }
            if (shouldShowRequest)
                return (<>
                    <div className="requestPage">
                        <ProctorProvidedFieldsBar
                            testState={testData}
                            valueOverrides={requestFieldValues}
                            mode="request"
                        />
                        {testData?.welcomeContent &&
                            <div className="instructions">{renderFormattedContent(testData.welcomeContent)}</div>}
                        <Request
                            companyId={testData.companyId || testData.companyId}
                            active={true}
                            updateSpinner={updateSpinner}
                            testState={testData}
                            onCreate={updateTestData}
                            showProctorFieldsBar={false}
                            onFieldValuesChange={setRequestFieldValues}
                        />
                    </div>
                </>)
            else if (testData.restartPage) {
                return (
                    <RestartPageOverlay
                        restartPage={testData.restartPage}
                        onContinue={onRestartContinueAsync}
                        isBusy={restartContinueBusy}
                    />
                );
            }
            else {
                return (<>
                    <TestSections
                        testState={testData}
                        updateTestState={updateTestData}
                        updateSpinner={updateSpinner}
                    />
                </>
                );
            }
        }
    }

    return (
        <div className="startPage">
            {showSpinner && (
                <div className="overlay">
                    <div className="spinner"></div>
                </div>
            )}
            <>
                {errorMessages.length > 0 &&
                    <div className="errors">
                        {errorMessages.map((msg, index) => (
                            <p key={index}>{parseSanitizedHtml(msg)}</p>
                        ))}
                    </div>
                }
                {renderActivity()}
            </>
        </div>
    );
}
